import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useConversionFunnel } from '@/hooks/useAdminAnalytics';
import { Card } from './Card';
import { DateRange } from '@/types/admin/analytics';

// Professional color palette for conversion stages
const STAGE_COLORS: Record<string, string> = {
  'Free → Pro': '#3b82f6',         // blue
  'Free → Enterprise': '#8b5cf6',  // violet
  'Pro → Enterprise': '#10b981',   // emerald
  'Free → Standard': '#f59e0b',    // amber
  'Standard → Enterprise': '#ef4444', // red
};

// Convert DateRange to days
function dateRangeToDays(dateRange: DateRange): number | undefined {
  const match = dateRange.start.match(/^-(\d+)d$/);
  if (match) return parseInt(match[1], 10);
  return undefined; // all time
}

interface Props {
  dateRange: DateRange;
}

/**
 * Conversion Funnel Chart
 * Professional visualization of user upgrade paths between subscription tiers
 */
export function ConversionFunnelChart({ dateRange }: Props) {
  const days = dateRangeToDays(dateRange);
  const { data, isLoading, error } = useConversionFunnel(days);

  const chartData = (data || []).map((item, index) => {
    const stageName = `${item.from_plan} → ${item.to_plan}`;
    return {
      ...item,
      name: stageName,
      color: STAGE_COLORS[stageName] || ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'][index % 5],
    };
  });

  // Calculate max rate for better visualization
  const maxRate = Math.max(...chartData.map(d => d.conversion_rate), 0);

  const FunnelIcon = (
    <svg className="w-5 h-5 text-brand-electric-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1M9 7h6m-6 4h6m-6 4h6M5 20h14a1 1 0 001-1V7a1 1 0 00-1-1H5a1 1 0 00-1 1v12a1 1 0 001 1z" />
    </svg>
  );

  return (
    <Card
      title="Conversion Funnel"
      subtitle="User upgrade paths and conversion rates between plans"
      isLoading={isLoading}
      error={error?.message || null}
      icon={FunnelIcon}
      accentColor="from-violet-500 to-purple-500"
    >
      {chartData.length === 0 && !isLoading && !error ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <svg className="w-16 h-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1M9 7h6m-6 4h6m-6 4h6M5 20h14a1 1 0 001-1V7a1 1 0 00-1-1H5a1 1 0 00-1 1v12a1 1 0 001 1z" />
          </svg>
          <p className="text-sm font-medium">No conversion data yet</p>
          <p className="text-xs mt-1 opacity-60">Conversion metrics will appear when users upgrade plans</p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 140, bottom: 5 }}>
              <defs>
                {chartData.map((item, idx) => (
                  <linearGradient key={`gradient-${idx}`} id={`gradient-${idx}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={item.color} stopOpacity={0.7} />
                    <stop offset="100%" stopColor={item.color} stopOpacity={1} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                type="number"
                domain={[0, 100]}
                label={{ value: 'Conversion Rate (%)', position: 'insideBottomRight', offset: -5 }}
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 12, fill: '#1e293b', fontWeight: 500 }}
                width={140}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value: any, name: string) => {
                  if (name === 'conversion_rate') {
                    return [typeof value === 'number' ? `${value.toFixed(2)}%` : value, 'Conversion Rate'];
                  }
                  if (name === 'total_upgrades') {
                    return [typeof value === 'number' ? value.toLocaleString() : value, 'Total Upgrades'];
                  }
                  return value;
                }}
                contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
                  padding: '12px 16px',
                }}
                labelFormatter={(label: any) => label}
              />
              <Bar dataKey="conversion_rate" radius={[0, 12, 12, 0]} barSize={32}>
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={chartData[index].color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Summary stats with progress */}
          <div className="mt-6 space-y-3">
            {chartData.map((item: any) => {
              const percentage = (item.conversion_rate / maxRate) * 100;
              return (
                <div
                  key={item.from_plan + item.to_plan}
                  className="p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm font-semibold text-gray-900">{item.name}</span>
                    </div>
                    <span className="text-lg font-bold" style={{ color: item.color }}>
                      {item.conversion_rate.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {item.total_upgrades} user{item.total_upgrades !== 1 ? 's' : ''} upgraded
                  </p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}
